import { Link } from "react-router-dom";
import { Mail, Instagram, ArrowUpRight, Send, Phone } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BRAND, NAV_LINKS } from "../data/site";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const FOOTER_SERVICES = [
  { label: "Website Development", slug: "website-development" },
  { label: "Google Business Profile", slug: "google-business-profile" },
  { label: "Social Media Marketing", slug: "social-media-marketing" },
  { label: "Branding & Logo Design", slug: "logo-design" },
  { label: "SEO Services", slug: "seo-services" },
  { label: "AI Business Solutions", slug: "ai-business-solutions" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, { email });
      toast.success("You're subscribed! We'll keep you posted.");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail === "Email already subscribed" ? "You're already on the list." : "Subscription failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[#0A0A0A] border-t border-white/10 text-gray-300">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-10">
        {/* CTA block */}
        <div className="rounded-3xl p-8 md:p-12 mb-16 bg-gradient-brand glow-blue relative overflow-hidden">
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-bold text-[#0A0A0A] leading-tight">
                Ready to grow your business online?
              </h3>
              <p className="text-black/70 mt-2 text-lg">Get your free consultation today — no obligations.</p>
            </div>
            <Link to="/contact" data-testid="footer-cta-button">
              <Button className="bg-white text-[#0A0A0A] hover:bg-[#F5D67B]/20 rounded-full h-12 px-7 font-bold text-base">
                Start Now <ArrowUpRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img src={BRAND.logoMark} alt="ZD" className="h-12 w-12 rounded-lg bg-white/5 p-1" />
              <div>
                <div className="font-display text-xl font-bold text-white">Zyvora Digital</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Build · Market · Scale</div>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-gray-400 mb-5">
              We build professional websites, strengthen online presence, and create digital growth systems for ambitious businesses.
            </p>
            <form onSubmit={onSubscribe} className="flex gap-2" data-testid="newsletter-form">
              <Input
                data-testid="newsletter-email-input"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-11 rounded-full"
              />
              <Button
                data-testid="newsletter-submit-button"
                type="submit"
                disabled={loading}
                className="bg-gradient-brand text-[#0A0A0A] rounded-full h-11 px-4"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>

          <div>
            <h4 className="font-display text-white font-semibold mb-5">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.path}>
                  <Link
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    to={l.path}
                    className="hover:text-white transition"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-white font-semibold mb-5">Services</h4>
            <ul className="space-y-3 text-sm">
              {FOOTER_SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    data-testid={`footer-service-${s.slug}`}
                    to={`/services#${s.slug}`}
                    className="hover:text-white transition"
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-white font-semibold mb-5">Contact</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <ul className="space-y-1">
                    {BRAND.phones.map((p, i) => (
                      <li key={i}>
                        <a
                          data-testid={`footer-phone-${i}`}
                          href={`tel:${p.tel}`}
                          className="hover:text-white transition"
                        >
                          {p.value}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-[#D4AF37] mt-1 shrink-0" />
                <a
                  data-testid="footer-email-link"
                  href={`mailto:${BRAND.email}`}
                  className="hover:text-white transition break-all"
                >
                  {BRAND.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Instagram className="h-4 w-4 text-[#D4AF37] mt-1 shrink-0" />
                <a
                  data-testid="footer-instagram-link"
                  href={BRAND.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-white transition"
                >
                  {BRAND.instagram}
                </a>
              </li>
              <li className="text-gray-400">
                <div className="text-white font-medium mb-1">Business Hours</div>
                {BRAND.businessHours}
              </li>
              <li className="text-gray-400">
                <div className="text-white font-medium mb-1">Response Time</div>
                {BRAND.responseTime}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
          <p className="text-gray-500">© 2026 Zyvora Digital. All Rights Reserved.</p>
          <p className="text-gray-400">Helping Businesses Grow Online <span className="text-[#D4AF37]">↗</span></p>
        </div>
      </div>
    </footer>
  );
}
